import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { StickyHeader, FAQs, Footer } from "./components/landing-new";
import { isLoggedIn, useGlobalState } from "../GlobalStates";
import axios from "../axios";
import { useToast } from "../components/common/useToast";
import Modal from "../components/common/Modal";
import {
  Sparkles,
  HandshakeIcon,
  DollarSign,
  Star,
  Target,
  Users,
  Globe,
  Ticket,
  Zap,
  Network,
  BookOpen,
  Trophy,
  Info,
} from "lucide-react";
import {
  landingVideoDesktop,
  landingVideoMobile,
  landingImageDesktop,
  landingImageMobile,
} from "../utils/constants";
import { useTabletOrMobile } from "../hooks/responsive";
import { pastLeaders } from "../constants/leaders";
import authAxios from "../authAxios";

const LeaderInvitation = () => {
  const { invitation_key } = useParams();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const toast = useToast();
  const [showDuckTieringModal, setShowDuckTieringModal] = useState(false);
  const isMobile = useTabletOrMobile();

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await authAxios.get(`/v1/leader-invitation/${invitation_key}`);
        setInvitation(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load invitation");
      } finally {
        setLoading(false);
      }
    };

    if (invitation_key) {
      fetchInvitation();
    }
  }, [invitation_key]);

  const showToast = (message, type) => toast.show(message, { type });

  const getLoginRedirectUrl = () => {
    const originalPath = window.location.pathname + window.location.search;
    return `/login?reroute=${encodeURIComponent(originalPath)}`;
  };

  const handleAcceptInvitation = async () => {
    if (!isLoggedIn(user)) {
      // Redirect to login with reroute parameter
      window.location = getLoginRedirectUrl();
      return;
    }

    setAccepting(true);
    try {
      await authAxios.post("/v1/leader-invitation/accept", {
        invitation_key,
      });
      // Show success toast
      showToast(
        "Congratulations! You've successfully accepted the leader invitation! Book your first trip as a leader now!",
        "celebration"
      );
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to accept invitation", "error");
      setAccepting(false);
    }
  };

  const handleRequestAgain = async () => {
    if (!isLoggedIn(user)) {
      // Redirect to login with reroute parameter
      window.location = getLoginRedirectUrl();
      return;
    }

    setRequesting(true);
    try {
      await authAxios.post("/v1/leader-invitation/request-again", {
        invitation_key,
      });
      showToast(
        "Request submitted! We'll review your request and get back to you soon.",
        "celebration"
      );
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to submit request", "error");
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <>
        <StickyHeader withoutCta />
        <div className="tw-relative tw-min-h-[calc(100vh)] tw-overflow-hidden">
          {/* Background Video/Image */}
          <div className="tw-absolute tw-inset-0">
            <video autoPlay muted loop playsInline className="tw-h-full tw-w-full tw-object-cover">
              <source src={isMobile ? landingVideoMobile : landingVideoDesktop} type="video/mp4" />
            </video>
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/60 tw-via-black/40 tw-to-black/60"></div>
          </div>

          {/* Loading Content */}
          <div className="tw-relative tw-flex tw-min-h-[calc(100vh-55px)] tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-16 md:tw-px-16 lg:tw-px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="tw-text-center"
            >
              {/* Loading Text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <h1 className="tw-mb-4 tw-text-[32px] tw-font-extrabold tw-leading-tight tw-text-white md:tw-text-[56px]">
                  Preparing your
                  <br />
                  <span className="tw-text-primary-main">invitation</span>
                </h1>
                <p className="tw-mx-auto tw-max-w-2xl tw-text-base tw-text-white/90 md:tw-text-xl">
                  Just a moment while we gather all the details for your leader invitation...
                </p>
              </motion.div>

              {/* Loading Dots */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="tw-mt-8 tw-flex tw-justify-center tw-gap-2"
              >
                {[0, 1, 2].map((index) => (
                  <motion.div
                    key={index}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: index * 0.2,
                    }}
                    className="tw-h-3 tw-w-3 tw-rounded-full tw-bg-primary-main"
                  />
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <StickyHeader withoutCta />
        <div className="tw-relative tw-min-h-[calc(100vh)] tw-overflow-hidden">
          {/* Background Video/Image */}
          <div className="tw-absolute tw-inset-0">
            <video autoPlay muted loop playsInline className="tw-h-full tw-w-full tw-object-cover">
              <source src={isMobile ? landingVideoMobile : landingVideoDesktop} type="video/mp4" />
            </video>
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/60 tw-via-black/40 tw-to-black/60"></div>
          </div>

          {/* Error Content */}
          <div className="tw-relative tw-flex tw-min-h-[calc(100vh-55px)] tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-16 md:tw-px-16 lg:tw-px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="tw-text-center"
            >
              {/* Error Icon */}
              <div className="tw-mb-8 tw-flex tw-justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
                  className="tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500/20"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="tw-h-10 tw-w-10 tw-text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </motion.div>
              </div>

              {/* Error Text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <h1 className="tw-mb-4 tw-text-[32px] tw-font-extrabold tw-leading-tight tw-text-white md:tw-text-[56px]">
                  Oops! Something
                  <br />
                  <span className="tw-text-red-400">went wrong</span>
                </h1>
                <p className="tw-mx-auto tw-mb-8 tw-max-w-2xl tw-text-base tw-text-white/90 md:tw-text-xl">
                  {error}
                </p>
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <Link to={`/beginner-leader-trips?invitation_key=${invitation_key}`}>
                  <button className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-8 tw-py-4 tw-text-lg tw-font-bold tw-text-white tw-shadow-lg tw-transition-all hover:tw-scale-105 hover:tw-shadow-xl">
                    Browse Available Trips
                  </button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <StickyHeader withoutCta />
      <HeroSection
        invitation={invitation}
        onRequestAgain={handleRequestAgain}
        requesting={requesting}
        user={user}
        invitation_key={invitation_key}
      />
      <WhatIsLeaderProgramme />
      {/* <LeaderTestimonials /> */}
      {/* <WhyInviteYou /> */}
      {invitation?.reviews && invitation.reviews.length > 0 && (
        <PastTripReviews reviews={invitation.reviews} />
      )}
      <WhatLeadersDo />
      <LeaderPerks />
      {invitation?.status === "pending" && (
        <AcceptInvitationSection
          invitation={invitation}
          onAccept={handleAcceptInvitation}
          accepting={accepting}
          user={user}
          onShowDuckTiering={() => setShowDuckTieringModal(true)}
          invitation_key={invitation_key}
        />
      )}
      <LeaderFAQs />
      <Footer />

      {/* Duck Tiering Modal */}
      <DuckTieringModal
        open={showDuckTieringModal}
        onClose={() => setShowDuckTieringModal(false)}
      />

    </>
  );
};

const HeroSection = ({ invitation, onRequestAgain, requesting, user, invitation_key }) => {
  const isMobile = useTabletOrMobile();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [useVideo, setUseVideo] = useState(true);

  const getStatusContent = () => {
    if (!invitation) return null;

    switch (invitation.status) {
      case "expired":
        return {
          title: "This invitation has expired",
          subtitle: "Don't worry! You can still request to become a leader.",
          showButton: true,
          buttonText: requesting ? "Requesting..." : "Request Invitation Again",
          buttonAction: onRequestAgain,
          buttonDisabled: requesting,
        };
      case "accepted":
      case "used":
        return {
          title: "This invitation is no longer valid",
          subtitle:
            "This invitation link has already been used. If you're looking to lead more trips, check out our available opportunities.",
          showButton: true,
          buttonText: "Browse Available Trips",
          buttonAction: () =>
            (window.location.href = `/beginner-leader-trips?invitation_key=${invitation_key}`),
          buttonDisabled: false,
        };
      default:
        return {
          title: "We think you'd be a great leader",
          subtitle:
            "Based on your journey with Sotravel, we believe you have what it takes to lead amazing adventures and inspire fellow travelers.",
          showButton: false,
        };
    }
  };

  const statusContent = getStatusContent();

  const handleVideoError = () => {
    setUseVideo(false);
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const scrollToNextSection = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth",
    });
  };

  return (
    <div className="tw-relative tw-h-screen tw-overflow-hidden">
      {/* Background Video/Image */}
      <div className="tw-absolute tw-inset-0">
        {useVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="tw-h-full tw-w-full tw-object-cover"
            onError={handleVideoError}
            onLoadedData={handleVideoLoad}
            style={{ opacity: videoLoaded ? 1 : 0 }}
          >
            <source src={isMobile ? landingVideoMobile : landingVideoDesktop} type="video/mp4" />
          </video>
        ) : (
          <img
            src={isMobile ? landingImageMobile : landingImageDesktop}
            alt="Leader Hero"
            className="tw-h-full tw-w-full tw-object-cover"
          />
        )}
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/60 tw-via-black/40 tw-to-black/60"></div>
      </div>

      {/* Content */}
      <div className="tw-relative tw-flex tw-h-screen tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-py-16 md:tw-px-16 lg:tw-px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="tw-text-center"
        >
          <h1 className="tw-mb-6 tw-text-[32px] tw-font-extrabold tw-leading-tight tw-text-white md:tw-text-[56px]">
            {statusContent.title.includes("great leader") ? (
              <>
                We think you'd be a great
                <br />
                <span className="tw-text-primary-main">Sotravel leader</span>
              </>
            ) : (
              statusContent.title
            )}
          </h1>
          <p className="tw-mx-auto tw-mb-8 tw-max-w-2xl tw-text-base tw-text-white/90 md:tw-text-xl">
            {statusContent.subtitle}
          </p>

          {/* Status-specific button */}
          {statusContent.showButton && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="tw-mt-8"
            >
              <button
                onClick={statusContent.buttonAction}
                disabled={statusContent.buttonDisabled}
                className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-8 tw-py-4 tw-text-lg tw-font-bold tw-text-white tw-shadow-lg tw-transition-all hover:tw-scale-105 hover:tw-shadow-xl disabled:tw-opacity-50"
              >
                {statusContent.buttonText}
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Scroll indicator - only show for pending status */}
        {invitation?.status === "pending" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="tw-absolute tw-bottom-8 tw-left-1/2 tw--translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              onClick={scrollToNextSection}
              className="tw-cursor-pointer tw-text-white/60 tw-transition-colors hover:tw-text-white/90"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="tw-h-8 tw-w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const WhatIsLeaderProgramme = () => {
  return (
    <div className="tw-bg-[#EBFAFF] tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-16 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-4xl tw-text-center">
        <h2 className="tw-mb-7 tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          What is a Sotravel leader?
        </h2>
        <div style={{ width: "100%", marginBottom: "2rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <img style={{ height: "27rem", width: "40rem", objectFit: "cover", borderRadius: "1rem" }} src="https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/1346309image_2025-10-11_01-23-34.png" />
        </div>
        <p className="tw-mb-8 tw-text-base tw-text-gray-700 md:tw-text-lg">
          Sotravel leaders are passionate travelers who will ensure you get the most out of your travel experience. Think of a leader as that friend who takes care of organizing everything—keeping track of time, booking excursions and activities, suggesting places to eat, and meanwhile, keeping everyone’s spirits high. In essence, a Sotravel leader is the best travel companion you could wish for.
        </p>
      </div>
    </div>
  );
};

const LeaderTestimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Use real leader testimonials from shared constants
  const testimonials = pastLeaders.map((leader) => ({
    name: leader.name,
    trip: leader.location,
    image: leader.image,
    quote: leader.quote,
  }));

  return (
    <div className="tw-bg-white tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-20 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-6xl">
        <h2 className="tw-mb-4 tw-text-center tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          Hear from Our Leaders
        </h2>
        <p className="tw-mb-12 tw-text-center tw-text-base tw-text-primary-main md:tw-text-lg">
          Real stories from people who've led amazing adventures
        </p>

        <div className="tw-relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-primary-main/10 tw-to-primary-main/5 tw-p-8 md:tw-p-12"
            >
              <div className="tw-grid tw-gap-8 md:tw-grid-cols-2 md:tw-items-center">
                <div>
                  <div className="tw-mb-6 tw-text-4xl tw-text-primary-main">"</div>
                  <p className="tw-mb-6 tw-text-lg tw-italic tw-text-gray-700 md:tw-text-xl">
                    {testimonials[currentIndex].quote}
                  </p>
                  <div>
                    <div className="tw-text-xl tw-font-bold">{testimonials[currentIndex].name}</div>
                    <div className="tw-text-sm tw-text-gray-600">
                      Trip Leader - {testimonials[currentIndex].trip}
                    </div>
                  </div>
                </div>
                <div className="tw-relative tw-h-[300px] tw-overflow-hidden tw-rounded-2xl md:tw-h-[400px]">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="tw-h-full tw-w-full tw-object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation dots */}
          <div className="tw-mt-6 tw-flex tw-justify-center tw-gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`tw-h-3 tw-w-3 tw-rounded-full tw-transition-all ${index === currentIndex ? "tw-w-8 tw-bg-primary-main" : "tw-bg-gray-300"
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const WhyInviteYou = () => {
  return (
    <div className="tw-bg-[white] tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-20 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-4xl tw-text-center">
        <h2 className="tw-mb-6 tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          Why did we invite you?
        </h2>
        <p className="tw-mb-12 tw-text-base tw-text-gray-700 md:tw-text-lg">
          We've been watching your journey, and here's what stood out to us
        </p>

        <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
          <div className="tw-rounded-2xl tw-bg-white tw-p-6 tw-text-left tw-shadow-sm">
            <div className="tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-100">
              <Star className="tw-h-6 tw-w-6 tw-text-amber-600" />
            </div>
            <h3 className="tw-mb-2 tw-text-lg tw-font-bold">Excellent Reviews</h3>
            <p className="tw-text-sm tw-text-gray-600">
              Your past trip participants have given you glowing reviews, showing your ability to
              create positive experiences
            </p>
          </div>

          <div className="tw-rounded-2xl tw-bg-white tw-p-6 tw-text-left tw-shadow-sm">
            <div className="tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100">
              <Target className="tw-h-6 tw-w-6 tw-text-blue-600" />
            </div>
            <h3 className="tw-mb-2 tw-text-lg tw-font-bold">Active Participation</h3>
            <p className="tw-text-sm tw-text-gray-600">
              You've been an engaged member of the Sotravel community, contributing to the vibrant
              atmosphere
            </p>
          </div>

          <div className="tw-rounded-2xl tw-bg-white tw-p-6 tw-text-left tw-shadow-sm">
            <div className="tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100">
              <Users className="tw-h-6 tw-w-6 tw-text-green-600" />
            </div>
            <h3 className="tw-mb-2 tw-text-lg tw-font-bold">Natural Leader</h3>
            <p className="tw-text-sm tw-text-gray-600">
              We've noticed your natural ability to bring people together and create connections
            </p>
          </div>

          <div className="tw-rounded-2xl tw-bg-white tw-p-6 tw-text-left tw-shadow-sm">
            <div className="tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-purple-100">
              <Globe className="tw-h-6 tw-w-6 tw-text-purple-600" />
            </div>
            <h3 className="tw-mb-2 tw-text-lg tw-font-bold">Adventure Spirit</h3>
            <p className="tw-text-sm tw-text-gray-600">
              Your enthusiasm for exploration and new experiences is contagious and inspiring
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatLeadersDo = () => {
  return (
    <div className="tw-bg-white tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-20 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-4xl">
        <h2 className="tw-mb-6 tw-text-center tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          What do leaders actually do?
        </h2>
        <p className="tw-mb-12 tw-text-center tw-text-base tw-text-gray-700 md:tw-text-lg">
          Don't worry - it's easier than you think! Here's what you'll be doing
        </p>

        <div className="tw-space-y-6">
          <div className="tw-flex tw-gap-4 tw-rounded-2xl tw-bg-gradient-to-r tw-from-primary-main/10 tw-to-transparent tw-p-6">
            <div className="tw-flex-none">
              <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-main tw-text-xl tw-font-bold tw-text-white">
                1
              </div>
            </div>
            <div>
              <h3 className="tw-mb-2 tw-text-xl tw-font-bold">Bring the Vibes</h3>
              <p className="tw-text-gray-600">
                Create an inclusive, fun atmosphere where everyone feels welcome. Your energy sets
                the tone for the entire trip! Be the person who makes everyone feel comfortable and
                excited.
              </p>
            </div>
          </div>

          <div className="tw-flex tw-gap-4 tw-rounded-2xl tw-bg-gradient-to-r tw-from-primary-main/10 tw-to-transparent tw-p-6">
            <div className="tw-flex-none">
              <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-main tw-text-xl tw-font-bold tw-text-white">
                2
              </div>
            </div>
            <div>
              <h3 className="tw-mb-2 tw-text-xl tw-font-bold">Keep things organised</h3>
              <p className="tw-text-gray-600">
                Help coordinate activities, manage timing, and ensure smooth logistics. Don't worry
                - we provide detailed documents and you'll have our support every step of the way!
              </p>
            </div>
          </div>

          <div className="tw-flex tw-gap-4 tw-rounded-2xl tw-bg-gradient-to-r tw-from-primary-main/10 tw-to-transparent tw-p-6">
            <div className="tw-flex-none">
              <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-main tw-text-xl tw-font-bold tw-text-white">
                3
              </div>
            </div>
            <div>
              <h3 className="tw-mb-2 tw-text-xl tw-font-bold">Bridge the gap between participants and our guides</h3>
              <p className="tw-text-gray-600">
                What the guide can't do, you will step out to help! This includes taking care of rooming, ensuring that the group is always on time, and being a friendly face.
              </p>
            </div>
          </div>

          <div className="tw-flex tw-gap-4 tw-rounded-2xl tw-bg-gradient-to-r tw-from-primary-main/10 tw-to-transparent tw-p-6">
            <div className="tw-flex-none">
              <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-main tw-text-xl tw-font-bold tw-text-white">
                4
              </div>
            </div>
            <div>
              <h3 className="tw-mb-2 tw-text-xl tw-font-bold">Foster connections</h3>
              <p className="tw-text-gray-600">
                Help break the ice and encourage group bonding. Travel has the ability to forge meaningful connections - and you'll be the catalyst for those connections!
              </p>
            </div>
          </div>
        </div>

        <div className="tw-mt-8 tw-rounded-2xl tw-bg-primary-main/10 tw-p-6 tw-text-center">
          <p className="tw-text-lg tw-font-semibold tw-text-gray-800">💪 We've got your back!</p>
          <p className="tw-mt-2 tw-text-gray-600">
            Every trip has clear leader instructions you can read before deciding to lead, plus
            you'll have access to exclusive 24/7 support from our team.
          </p>
        </div>
      </div>
    </div>
  );
};

const LeaderPerks = () => {
  const perks = [
    {
      icon: DollarSign,
      iconColor: "tw-text-green-600",
      bgColor: "tw-bg-green-100",
      title: "Heavily discounted or free trips",
      description:
        "Yes, we know this is the biggest perk.. but there are many other perks too!",
    },
    {
      icon: Ticket,
      iconColor: "tw-text-purple-600",
      bgColor: "tw-bg-purple-100",
      title: "Invites to exclusive community events",
      description:
        "Exclusive invitations to Sotravel meetups, workshops, and annual leader summits around the world",
    },
    {
      icon: Zap,
      iconColor: "tw-text-amber-600",
      bgColor: "tw-bg-amber-100",
      title: "Priority trip selection",
      description:
        "Choose which trips you want to lead before they go public. Get first dibs on the most exciting destinations",
    },
    {
      icon: Network,
      iconColor: "tw-text-blue-600",
      bgColor: "tw-bg-blue-100",
      title: "Leader community",
      description:
        "Join an exclusive network of trip leaders. Share tips, stories, and build lasting friendships",
    },
  ];

  return (
    <div className="tw-bg-[#EBFAFF] tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-20 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-6xl">
        <h2 className="tw-mb-6 tw-text-center tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          The perks of being a leader
        </h2>
        <p className="tw-mb-12 tw-text-center tw-text-base tw-text-primary-main md:tw-text-lg">
          Leadership comes with some seriously awesome benefits
        </p>

        <div className="tw-grid tw-gap-6 md:tw-grid-cols-4 lg:tw-grid-cols-4">
          {perks.map((perk, index) => {
            const IconComponent = perk.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
              >
                <div
                  className={`tw-mb-4 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full ${perk.bgColor}`}
                >
                  <IconComponent className={`tw-h-6 tw-w-6 ${perk.iconColor}`} />
                </div>
                <h3 className="tw-mb-2 tw-text-lg tw-font-bold">{perk.title}</h3>
                <p className="tw-text-sm tw-text-gray-600">{perk.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DuckTieringModal = ({ open, onClose }) => {
  const duckTiers = [
    {
      name: "Junior Duck",
      image: require("../assets/images/juniorduckstatic.png"),
      color: "tw-text-yellow-600",
      bgColor: "tw-bg-yellow-50",
      borderColor: "tw-border-yellow-200",
      benefits: [
        "25% discount off trips",
        "Access to leader-only events",
        "Lead trips up to 4 days",
      ],
      requirements: [
        "Complete your first trip as a duckling",
        "Receive a leader recommendation",
        "Complete Junior duck quiz",
      ],
    },
    {
      name: "Senior Duck",
      image: require("../assets/images/seniorduckstatic.png"),
      color: "tw-text-blue-600",
      bgColor: "tw-bg-blue-50",
      borderColor: "tw-border-blue-200",
      benefits: [
        "50% discount off trips",
        "Lead any trip regardless of duration",
        "Ability to request new trip dates",
      ],
      requirements: [
        "Lead a trip as a Junior duck",
        "6+ participants complete trip reviews",
        "85%+ review score after 10 days",
      ],
    },
    {
      name: "Super Duck",
      image: require("../assets/images/superduckstatic.png"),
      color: "tw-text-purple-600",
      bgColor: "tw-bg-purple-50",
      borderColor: "tw-border-purple-200",
      benefits: [
        "Customizable discounts or earnings",
        "Invitation to lead maiden trips",
        "Build and lead custom trips",
        "Exclusive swag & more!",
      ],
      requirements: [
        "Lead a trip as a Senior duck",
        "6+ participants complete trip reviews",
        "85%+ review score after 10 days",
      ],
    },
  ];

  const headerContent = (
    <div>
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="tw-bg-gradient-to-r tw-from-primary-main tw-to-blue-600 tw-bg-clip-text tw-text-2xl tw-font-bold tw-text-transparent"
      >
        Our Duck Tiering System
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="tw-mt-2 tw-text-sm tw-text-gray-600"
      >
        As you grow as a leader, you'll progress through our duck tiers, unlocking amazing benefits
        along the way
      </motion.p>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      headerContent={headerContent}
      maxWidth="4xl"
      bodyPadding="tw-px-6 tw-py-6"
    >
      <div className="tw-space-y-6">
        {duckTiers.map((tier, index) => (
          <motion.div
            key={tier.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`tw-rounded-xl tw-border-2 ${tier.borderColor} ${tier.bgColor} tw-p-6 tw-shadow-sm`}
          >
            <div className="tw-flex tw-items-start tw-space-x-4">
              {/* Duck Image */}
              <div className="tw-flex-shrink-0">
                <motion.img
                  src={tier.image}
                  alt={tier.name}
                  className="tw-h-16 tw-w-16 tw-object-contain"
                  animate={{
                    y: [0, -3, 0],
                    rotate: [0, -1, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut",
                  }}
                />
              </div>

              {/* Content */}
              <div className="tw-flex-1">
                <h3 className={`tw-mb-3 tw-text-xl tw-font-bold ${tier.color}`}>{tier.name}</h3>

                <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                  {/* Benefits */}
                  <div>
                    <h4 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-900">
                      Benefits:
                    </h4>
                    <ul className="tw-list-inside tw-list-disc tw-space-y-1">
                      {tier.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="tw-text-sm tw-text-gray-600">
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h4 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-900">
                      Requirements:
                    </h4>
                    <ul className="tw-list-inside tw-list-disc tw-space-y-1">
                      {tier.requirements.map((requirement, reqIndex) => (
                        <li key={reqIndex} className="tw-text-sm tw-text-gray-600">
                          {requirement}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="tw-rounded-xl tw-bg-primary-main/10 tw-p-6 tw-pb-8 tw-text-center"
        >
          <p className="tw-text-lg tw-font-semibold tw-text-gray-800">
            🚀 Start your journey as a Junior Duck!
          </p>
          <p className="tw-mt-2 tw-text-gray-600">
            Accept your invitation to begin your leadership journey and work your way up through the tiers
          </p>
        </motion.div>
      </div>
    </Modal>
  );
};

const AcceptInvitationSection = ({ invitation, onAccept, accepting, user, onShowDuckTiering, invitation_key }) => {
  const [localAccepted, setLocalAccepted] = useState(false);
  const sectionRef = React.useRef(null);

  // Check if invitation is already accepted
  const isAccepted = invitation?.accepted_at || localAccepted;

  const getLoginRedirectUrl = () => {
    const originalPath = window.location.pathname + window.location.search;
    return `/login?reroute=${encodeURIComponent(originalPath)}`;
  };

  const handleAccept = () => {
    // If user is not logged in, redirect to login without triggering animation or calling onAccept
    if (!isLoggedIn(user)) {
      window.location = getLoginRedirectUrl();
      return;
    }

    // Only if user is logged in, trigger animation and call onAccept
    setLocalAccepted(true);
    onAccept();
  };

  if (!invitation) {
    return null;
  }

  return (
    <div className="tw-bg-gradient-to-br tw-from-primary-main/5 tw-via-white tw-to-primary-main/5 tw-px-4 tw-py-20 md:tw-px-16 lg:tw-px-24">
      <div className="tw-container tw-mx-auto tw-max-w-4xl">
        <motion.div
          ref={sectionRef}
          className="tw-space-y-12 tw-text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          {/* Heading */}
          <div className="tw-space-y-4">
            <h2 className="tw-text-balance tw-text-4xl tw-font-bold md:tw-text-5xl">
              The Best Way to Learn is to <span className="tw-text-primary-main">Try</span>
            </h2>
            <p className="tw-text-pretty tw-text-xl tw-text-gray-600">
              Here's ${invitation.added_credits} in credits to lead your first trip
            </p>
          </div>

          {/* Transformation Animation */}
          <div className="tw-relative tw-flex tw-min-h-[300px] tw-items-center tw-justify-center tw-py-12">
            <div className="tw-relative tw-flex tw-w-full tw-items-center tw-justify-center">
              {/* Egg - positioned absolutely */}
              <motion.div
                className="tw-absolute"
                initial={{ x: 0 }}
                animate={
                  isAccepted
                    ? { scale: 0, rotate: 360, opacity: 0, x: -100 }
                    : { scale: 1, rotate: 0, opacity: 1, x: -120 }
                }
                transition={{ duration: 0.8 }}
              >
                <div className="tw-relative tw-flex tw-h-40 tw-w-32 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-3xl tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-gray-100 tw-to-gray-200 tw-shadow-xl md:tw-h-48 md:tw-w-40">
                  <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-transparent tw-to-white/20" />
                  <motion.div
                    className="tw-text-6xl"
                    animate={{ rotate: [0, -5, 5, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    🥚
                  </motion.div>
                </div>
                <p className="tw-mt-4 tw-text-center tw-font-semibold tw-text-gray-600">Egg</p>
              </motion.div>

              {/* Arrow - fades out when accepted */}
              <motion.div
                className="tw-absolute"
                initial={{ opacity: 1, x: 0 }}
                animate={isAccepted ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="tw-text-primary-main"
                  animate={!isAccepted ? { x: [0, 10, 0] } : {}}
                  transition={{ repeat: Infinity, duration: 1 }}
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </motion.svg>
              </motion.div>

              {/* Duck - moves to center and scales up when accepted */}
              <motion.div
                className="tw-relative"
                initial={{ x: 120, scale: 0.8, opacity: 0.3 }}
                animate={
                  isAccepted
                    ? { x: 0, scale: 1.1, opacity: 1 }
                    : { x: 120, scale: 0.8, opacity: 0.3 }
                }
                transition={{ duration: 0.8, delay: isAccepted ? 0.3 : 0 }}
              >
                <div className="tw-relative tw-flex tw-h-40 tw-w-32 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-3xl tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-primary-main/10 tw-to-primary-main/5 tw-p-4 tw-shadow-xl md:tw-h-48 md:tw-w-40">
                  <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-transparent tw-to-white/20" />
                  <motion.div
                    className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
                    animate={isAccepted ? { y: [0, -10, 0] } : {}}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <motion.img
                      src={require("../assets/images/juniorduckstatic.png")}
                      alt="Junior Duck"
                      className="tw-h-24 tw-w-24 tw-object-contain"
                      animate={{
                        y: [0, -5, 0],
                        rotate: [0, -2, 2, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                      }}
                    />
                  </motion.div>
                </div>
                <p className="tw-mt-4 tw-text-center tw-font-semibold tw-text-primary-main">
                  Junior Duck
                </p>
              </motion.div>
            </div>
          </div>

          {/* CTA */}
          <motion.div
            className="tw-space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            {!isAccepted ? (
              <>
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-12 tw-py-6 tw-text-lg tw-font-bold tw-text-white tw-shadow-lg tw-transition-all hover:tw-scale-105 hover:tw-shadow-xl disabled:tw-opacity-50"
                >
                  {accepting
                    ? "Accepting..."
                    : user
                      ? `Accept $${invitation.added_credits} Credits`
                      : "Login to Accept"}
                </button>
                {invitation.expired_at && (
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-600">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="tw-h-4 tw-w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        Credits expire on {new Date(invitation.expired_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Duck Tiering Info Button */}
                    <button
                      onClick={onShowDuckTiering}
                      className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-primary-main/20 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-primary-main tw-transition-all hover:tw-border-primary-main/40 hover:tw-bg-primary-main/5"
                    >
                      <Info className="tw-h-4 tw-w-4" />
                      <span>Learn about our Duck Tiering System</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="tw-space-y-4"
              >
                <div className="tw-inline-block tw-rounded-full tw-bg-primary-main/10 tw-px-6 tw-py-3 tw-text-lg tw-font-semibold tw-text-primary-main">
                  ✨ Credits Activated! Welcome, Junior Duck!
                </div>
                <p className="tw-text-gray-600">
                  Your credits are ready to use. Browse trips and start your leadership journey!
                </p>
                <Link to={`/beginner-leader-trips?invitation_key=${invitation_key}`}>
                  <button className="hover:tw-bg-primary-dark tw-mt-4 tw-rounded-full tw-bg-primary-main tw-px-8 tw-py-3 tw-font-semibold tw-text-white tw-transition-all">
                    Lead your first trip
                  </button>
                </Link>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

const PastTripReviews = ({ reviews }) => {
  const shortenText = (text) => {
    const reviewMaxLength = 120;
    if (!text || text.length === 0) {
      const positiveWords = ["Awesome!", "Great!", "Amazing!", "Fantastic!"];
      return positiveWords[Math.floor(Math.random() * positiveWords.length)];
    }
    if (text.length > reviewMaxLength) {
      return `"${text.slice(0, reviewMaxLength)} ...`;
    }
    return `"${text}"`;
  };

  return (
    <div className="tw-bg-white tw-px-4 tw-py-16 md:tw-px-16 md:tw-py-20 lg:tw-px-24">
      <div className="tw-mx-auto tw-max-w-6xl">
        <h2 className="tw-mb-4 tw-text-center tw-text-[28px] tw-font-extrabold md:tw-text-[42px]">
          Why did we invite you?
        </h2>
        <p className="tw-mb-12 tw-text-center tw-text-base tw-text-primary-main md:tw-text-lg">
          Because you are a star, that's why.
        </p>

        <div className="tw-grid tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3">
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="tw-flex tw-flex-col tw-rounded-2xl tw-bg-gradient-to-br tw-from-primary-main/5 tw-to-primary-main/10 tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
            >
              <p className="tw-mb-4 tw-flex-1 tw-text-sm tw-italic tw-text-gray-700 md:tw-text-base">
                {shortenText(review.positive)}
              </p>
              <div className="tw-mt-auto tw-flex tw-items-center tw-gap-3">
                <div className="tw-relative tw-h-10 tw-w-10 tw-overflow-hidden tw-rounded-full">
                  <img
                    src={review.review_by_image}
                    alt={review.review_by_display_name || review.review_by_first_name}
                    className="tw-h-full tw-w-full tw-object-cover"
                  />
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-text-sm tw-font-semibold tw-text-gray-900">
                    {review.review_by_display_name || review.review_by_first_name}
                  </div>
                  {review.trip_name && (
                    <div className="tw-text-xs tw-leading-tight tw-text-gray-600">
                      You both were in the same {review.trip_name} trip
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {reviews.length > 0 && (
          <div className="tw-mt-8 tw-text-center">
            <p className="tw-text-sm tw-text-gray-600">
              These positive reviews show why we believe you'd make an excellent trip leader!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const LeaderFAQs = () => {
  const leaderFaqs = [
    {
      question: "How do I book a trip as a leader?",
      answer: (
        <p>
          Once you accept your invitation, you'll receive leader credits that you can use when
          booking trips. Simply browse available trips, select one you'd like to lead, and apply
          your credits at checkout. You'll see a special "Lead This Trip" option on eligible trips.
        </p>
      ),
    },
    {
      question: "Is there a training programme?",
      answer: (
        <p>
          Yes! We provide comprehensive training materials including video tutorials, leader
          guidelines, and best practices. You'll also have access to our leader community where
          experienced leaders share tips. Plus, our team is available 24/7 to support you before,
          during, and after your trip.
        </p>
      ),
    },
    {
      question: "What are leader credits?",
      answer: (
        <p>
          Leader credits are monetary credits that can be used towards booking trips as a leader.
          Each credit is worth $1 and can be applied to reduce the cost of your trip. Credits expire
          if not used within the specified timeframe, so make sure to book your trip before they
          expire!
        </p>
      ),
    },
    {
      question: "What happens if I accept but don't book a trip?",
      answer: (
        <p>
          If you accept the invitation but don't book a trip before the credits expire, the credits
          will be revoked. We encourage you to browse available trips and book one that excites you
          as soon as possible to make the most of this opportunity!
        </p>
      ),
    },
    {
      question: "Can I lead multiple trips?",
      answer: (
        <p>
          Absolutely! Once you successfully lead your first trip, you'll be able to lead as many
          trips as you'd like. Each trip you lead earns you more credits and perks, helping you
          climb the leader leaderboard and unlock even more benefits.
        </p>
      ),
    },
    {
      question: "If I have more questions, who do I contact?",
      answer: (
        <p>
          You can reach out to our team anytime at{" "}
          <a href="mailto:community@sotravel.com" className="tw-text-primary-main tw-underline">
            community@sotravel.com
          </a>{" "}
          or through our Telegram community at{" "}
          <a
            href="https://t.me/sotravel_official"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-primary-main tw-underline"
          >
            @sotravel_official
          </a>
          . We're here to help!
        </p>
      ),
    },
  ];

  return <FAQs customFaqs={leaderFaqs} />;
};

export default LeaderInvitation;
